import { useEffect, useMemo, useState } from "react";
import {
  BarChart3, Users, GitBranch, FolderKanban, TrendingUp, FileText,
  Loader2, RefreshCw, AlertTriangle, Sparkles, ArrowRight, Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";
import { BdPerformanceTab, PipelineTab, ProjectsRevenueTab, ForecastingTab } from "../components/reports/ReportTabs";
import SavedReportsTab from "../components/reports/SavedReportsTab";

// ── Reports & Analytics ──────────────────────────────────────────────────────
//
// A reporting layer over the existing modules: it summarises and links out,
// and never becomes a second place where the records live.
//
// Phase 1 is Overview. The other five tabs are listed because the filter bar
// and the period context belong to the workspace rather than to one tab — and
// because a tab that is coming is more honest than one that is hidden.
//
// Two rules the UI keeps throughout:
//   A null figure is shown as a dash, never as 0. The two mean different
//   things and a dashboard that confuses them misreports a quiet month as a
//   bad one.
//   Forecasts are visually separated from actuals and carry their assumptions.

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3, ready: true },
  { key: "bd", label: "BD Performance", icon: Users, ready: true },
  { key: "pipeline", label: "Sales Pipeline", icon: GitBranch, ready: true },
  { key: "projects", label: "Projects & Revenue", icon: FolderKanban, ready: true },
  { key: "forecasting", label: "Forecasting", icon: TrendingUp, ready: true },
  { key: "reports", label: "Reports", icon: FileText, ready: true },
] as const;

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "custom", label: "Custom Duration" },
];

// Where a chart segment leads. Kept as a table so a label and its destination
// cannot drift apart as the charts change.
const OUTCOME_STATUS: Record<string, string> = {
  "In Negotiation": "Negotiated",
  Won: "Won Lead",
  Lost: "Lost Lead",
};
const FUNNEL_ROUTE: Record<string, string> = {
  Bids: "/bids",
  Proposals: "/proposals",
  Questionnaires: "/questionnaires",
  Won: "/contacts?leadStatus=Won%20Lead",
};

const money = (n: number | null) => (n === null || n === undefined ? "—" : `$${Number(n).toLocaleString()}`);
const count = (n: number | null) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const pct = (n: number | null) => (n === null || n === undefined ? "—" : `${n}%`);

export default function ReportsPage() {
  const navigate = useNavigate();
  const { authUser } = useAuthStore();
  const [tab, setTab] = useState<string>("overview");

  const [filters, setFilters] = useState({
    period: "month",
    from: "",
    to: "",
    bd: "all",
    source: "all",
    tenant: "all",
    platform: "all",
    targetCycle: "all",
  });

  // Brands and platforms the viewer may filter by. Loaded once; a failure
  // leaves the filter off rather than blocking the page.
  const [brands, setBrands] = useState<any[]>([]);
  const [platforms, setPlatforms] = useState<any[]>([]);
  useEffect(() => {
    axiosInstance.get("/tenants").then((r) => setBrands(r.data?.tenants || [])).catch(() => setBrands([]));
    axiosInstance.get("/bids/platforms").then((r) => setPlatforms(r.data || [])).catch(() => setPlatforms([]));
  }, []);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get("/reports/overview", { params: filters });
      setData(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not build the report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const developers = data?.scope?.developers || [];
  const isManager = data?.scope?.isManager;

  const kpis = useMemo(() => {
    if (!data) return [];
    const k = data.kpis;
    return [
      { label: "Follow-ups overdue", value: count(k.followUps.overdue), tone: k.followUps.overdue > 0 ? "rose" : "slate", to: "/activities", sub: `${count(k.followUps.completed)} done · ${count(k.followUps.pending)} pending` },
      { label: "Bids", value: count(k.bids.count), tone: "slate", to: "/bids", sub: `${money(k.bids.value)} · ${count(k.connects.used)} connects` },
      { label: "Proposals", value: count(k.proposals), tone: "slate", to: "/proposals" },
      { label: "Questionnaires", value: `${count(k.questionnaires.completed)} / ${count(k.questionnaires.sent)}`, tone: "slate", to: "/questionnaires", sub: "returned / sent" },
      { label: "New clients", value: count(k.newClients), tone: "slate", to: "/contacts" },
      { label: "Deals won", value: count(k.dealsWon), tone: "emerald", to: "/contacts" },
      { label: "Projects created", value: count(k.projectsCreated), tone: "slate", to: "/projects" },
      { label: "Revenue collected", value: money(k.actualRevenue), tone: "emerald", to: "/admin/invoices", sub: "paid invoices" },
    ];
  }, [data]);

  const toneCls: Record<string, string> = {
    rose: "text-rose-600",
    emerald: "text-emerald-600",
    slate: "text-slate-800",
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Reports &amp; Analytics</h1>
        <p className="text-[13px] text-slate-500">
          Omni Workspace · Business Intelligence
          {data?.period && <> — {data.period.label}</>}
        </p>
      </div>

      {/* ── Global filters ── shared by every tab, so the period context does
          not reset when someone moves between them. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Reporting Period">
            <select
              value={filters.period}
              onChange={(e) => setFilters({ ...filters, period: e.target.value })}
              className={selectCls}
            >
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>

          {/* Only when it means something — two empty date boxes on every other
              option is clutter that has to be read and dismissed. */}
          {filters.period === "custom" && (
            <>
              <Field label="Start Date">
                <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className={selectCls} />
              </Field>
              <Field label="End Date">
                <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className={selectCls} />
              </Field>
            </>
          )}

          {isManager && (
            <Field label="Business Developer">
              <select value={filters.bd} onChange={(e) => setFilters({ ...filters, bd: e.target.value })} className={selectCls}>
                <option value="all">All BDs</option>
                {developers.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
          )}

          <Field label="Acquisition Source">
            <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} className={selectCls}>
              <option value="all">All Sources</option>
              <option value="freelance">Freelance Platforms</option>
              <option value="direct">Direct Clients</option>
            </select>
          </Field>

          {/* Measured separately from the reporting period on purpose: a BD
              can be judged on this week's activity while their target runs
              monthly, and collapsing the two answers the wrong question. */}
          <Field label="BD Target Cycle">
            <select value={filters.targetCycle} onChange={(e) => setFilters({ ...filters, targetCycle: e.target.value })} className={selectCls}>
              <option value="all">All Cycles</option>
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
            </select>
          </Field>

          {brands.length > 1 && (
            <Field label="Brand">
              <select value={filters.tenant} onChange={(e) => setFilters({ ...filters, tenant: e.target.value })} className={selectCls}>
                <option value="all">All Brands</option>
                {brands.map((b: any) => <option key={b._id} value={b._id}>{b.name}</option>)}
              </select>
            </Field>
          )}

          {platforms.length > 1 && (
            <Field label="Freelance Platform">
              <select value={filters.platform} onChange={(e) => setFilters({ ...filters, platform: e.target.value })} className={selectCls}>
                <option value="all">All Platforms</option>
                {platforms.map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
            </Field>
          )}

          <button
            type="button"
            onClick={load}
            disabled={loading || (filters.period === "custom" && (!filters.from || !filters.to))}
            className="px-4 py-2 rounded-lg bg-[#2462b2] text-white text-[13px] font-bold flex items-center gap-1.5 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Generate Report
          </button>

          {data?.period?.generatedAt && (
            <span className="text-[11px] text-slate-400 ml-auto flex items-center gap-1">
              <Clock size={11} /> Generated {new Date(data.period.generatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-2.5 text-[13px] font-semibold whitespace-nowrap flex items-center gap-1.5 border-b-2 -mb-px ${
                active ? "border-[#2462b2] text-[#2462b2]" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon size={14} /> {t.label}
              {!t.ready && <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">soon</span>}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-[13px] text-rose-800">{error}</div>
      )}

      {/* Each tab reads the same filters, so a period chosen once carries
          across the workspace without being restated. */}
      <BdPerformanceTab filters={filters} active={tab === "bd"} />
      <PipelineTab filters={filters} active={tab === "pipeline"} />
      <ProjectsRevenueTab filters={filters} active={tab === "projects"} />
      <ForecastingTab filters={filters} active={tab === "forecasting"} />
      <SavedReportsTab filters={filters} active={tab === "reports"} isManager={isManager} />

      {tab === "overview" && data && (
        <>
          {/* ── AI executive summary ── */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <SummaryCard title="Performance Highlights" items={data.summary.highlights} tone="emerald" />
            <SummaryCard title="Attention Required" items={data.summary.attention} tone="rose" />
            <SummaryCard title="Forecast Outlook" items={data.summary.outlook} tone="violet" forecast />
            <SummaryCard title="Recommended Actions" items={data.summary.actions} tone="sky" />
          </div>
          {data.summary.generatedBy === "fallback" && (
            <p className="text-[11px] text-amber-700">
              Written without AI — the figures are the same, the wording is plainer.
            </p>
          )}

          {/* ── KPI cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {kpis.map((k) => (
              <button
                key={k.label}
                type="button"
                onClick={() => k.to && navigate(k.to)}
                className="text-left rounded-xl border border-slate-200 bg-white p-3.5 hover:border-[#2462b2]/40 hover:shadow-sm transition group"
              >
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{k.label}</p>
                <p className={`text-[22px] font-extrabold mt-0.5 ${toneCls[k.tone]}`}>{k.value}</p>
                {k.sub && <p className="text-[11px] text-slate-500">{k.sub}</p>}
                <span className="text-[10px] text-[#2462b2] opacity-0 group-hover:opacity-100 flex items-center gap-0.5 mt-1">
                  Open <ArrowRight size={9} />
                </span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* ── Outcomes ── */}
            <Panel title="Opportunity outcomes" sub={`Win rate ${pct(data.pipeline.winRate)}${data.pipeline.closed ? ` · ${data.pipeline.closed} closed` : ""}`}>
              {data.charts.outcomes.every((o: any) => !o.value) ? (
                <Empty>Nothing has moved through the pipeline in this period.</Empty>
              ) : (
                <div className="space-y-2">
                  {data.charts.outcomes.map((o: any) => {
                    const total = data.charts.outcomes.reduce((s: number, x: any) => s + x.value, 0) || 1;
                    const colour = o.label === "Won" ? "bg-emerald-500" : o.label === "Lost" ? "bg-rose-500" : "bg-amber-500";
                    return (
                      // Clicking a segment opens the contacts it stands for,
                      // with the matching status applied.
                      <button
                        key={o.label}
                        type="button"
                        onClick={() => navigate(`/contacts?leadStatus=${encodeURIComponent(OUTCOME_STATUS[o.label] || "")}`)}
                        className="w-full text-left group"
                      >
                        <div className="flex justify-between text-[12px]">
                          <span className="text-slate-600 group-hover:text-[#2462b2]">{o.label}</span>
                          <span className="font-bold text-slate-800">{count(o.value)}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                          <div className={`h-full ${colour} rounded-full`} style={{ width: `${(o.value / total) * 100}%` }} />
                        </div>
                      </button>
                    );
                  })}
                  <p className="text-[10.5px] text-slate-400 pt-1">
                    Win rate counts closed deals only — open opportunities are excluded.
                  </p>
                </div>
              )}
            </Panel>

            {/* ── Funnel ── */}
            <Panel title="Sales funnel" sub="Activity through to closing">
              <div className="space-y-2">
                {data.charts.funnel.map((f: any, i: number) => {
                  const top = data.charts.funnel[0]?.value || 1;
                  return (
                    <button
                      key={f.label}
                      type="button"
                      onClick={() => {
                        const to = FUNNEL_ROUTE[f.label];
                        if (to) navigate(to);
                      }}
                      className="w-full text-left group"
                    >
                      <div className="flex justify-between text-[12px]">
                        <span className="text-slate-600 group-hover:text-[#2462b2]">{f.label}</span>
                        <span className="font-bold text-slate-800">{count(f.value)}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                        <div className="h-full bg-[#2462b2] rounded-full" style={{ width: `${Math.min(100, (f.value / top) * 100)}%`, opacity: 1 - i * 0.18 }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </Panel>

            {/* ── Forecast, kept apart from the actuals above ── */}
            <Panel title="Forecast" sub="An estimate, not booked revenue" tone="violet">
              {data.forecast.available ? (
                <>
                  <p className="text-[24px] font-extrabold text-violet-700">{money(data.forecast.weightedPipeline)}</p>
                  <p className="text-[11px] text-slate-500">weighted pipeline</p>
                  <ul className="mt-2 space-y-0.5">
                    {(data.forecast.assumptions || []).map((a: string, i: number) => (
                      <li key={i} className="text-[11px] text-slate-500 pl-2.5 relative">
                        <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-slate-300" />{a}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10.5px] text-violet-700 mt-2 font-semibold">
                    Confidence: {data.forecast.confidence}
                  </p>
                </>
              ) : (
                <Empty>
                  <AlertTriangle size={13} className="inline mr-1 text-amber-500" />
                  {data.forecast.reason}
                </Empty>
              )}
            </Panel>
          </div>

          {/* ── Targets ── only for a single BD; an average across people is a
              number nobody is accountable for. */}
          {data.targets?.length > 0 && (
            <Panel title="Target achievement" sub="Current period">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {data.targets.map((t: any) => (
                  <div key={t.id} className={`rounded-xl border p-3 ${t.behind ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">{t.metricLabel}</p>
                    <p className="text-[18px] font-extrabold text-slate-900">
                      {t.unit === "currency" ? money(t.achieved) : count(t.achieved)}
                      <span className="text-[12px] font-semibold text-slate-400">
                        {" / "}{t.unit === "currency" ? money(t.target) : count(t.target)}
                      </span>
                    </p>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                      <div className={`h-full rounded-full ${t.behind ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, t.pct ?? 0)}%` }} />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">{t.periodLabel} · {t.status}</p>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {/* ── Activity trend ── */}
          <Panel title="Activity trend" sub="Bids submitted per day">
            {data.charts.activityTrend.every((d: any) => !d.bids) ? (
              <Empty>No bids recorded in this period.</Empty>
            ) : (
              <div className="flex items-end gap-0.5 h-24">
                {data.charts.activityTrend.map((d: any) => {
                  const max = Math.max(...data.charts.activityTrend.map((x: any) => x.bids), 1);
                  return (
                    <div
                      key={d.date}
                      title={`${d.date}: ${d.bids} bid${d.bids === 1 ? "" : "s"}`}
                      className="flex-1 bg-[#2462b2]/80 hover:bg-[#2462b2] rounded-t min-h-[2px]"
                      style={{ height: `${(d.bids / max) * 100}%` }}
                    />
                  );
                })}
              </div>
            )}
          </Panel>
        </>
      )}

      {tab === "overview" && loading && !data && (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 flex justify-center">
          <Loader2 size={20} className="animate-spin text-slate-400" />
        </div>
      )}
    </div>
  );
}

// ── small pieces ─────────────────────────────────────────────────────────────

const selectCls =
  "px-3 py-2 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-700 outline-none focus:border-[#2462b2] focus:ring-1 focus:ring-[#2462b2]/30";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</label>
    {children}
  </div>
);

const Panel = ({ title, sub, tone, children }: { title: string; sub?: string; tone?: string; children: React.ReactNode }) => (
  <div className={`rounded-2xl border bg-white p-4 ${tone === "violet" ? "border-violet-200" : "border-slate-200"}`}>
    <p className="text-[13px] font-bold text-slate-800">{title}</p>
    {sub && <p className="text-[11px] text-slate-500 mb-2.5">{sub}</p>}
    {children}
  </div>
);

const Empty = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[12px] text-slate-400 py-2">{children}</p>
);

const SummaryCard = ({ title, items, tone, forecast }: { title: string; items: string[]; tone: string; forecast?: boolean }) => {
  const tones: Record<string, string> = {
    emerald: "border-emerald-200 bg-emerald-50/50",
    rose: "border-rose-200 bg-rose-50/50",
    violet: "border-violet-200 bg-violet-50/50",
    sky: "border-sky-200 bg-sky-50/50",
  };
  return (
    <div className={`rounded-2xl border p-3.5 ${tones[tone]}`}>
      <p className="text-[12px] font-bold text-slate-800 flex items-center gap-1.5">
        <Sparkles size={12} /> {title}
      </p>
      {/* Predictions are labelled as such wherever they appear, so a forecast
          is never read as something that has already happened. */}
      {forecast && items.length > 0 && (
        <p className="text-[10px] font-bold uppercase tracking-wide text-violet-600 mt-0.5">Estimate</p>
      )}
      {items.length === 0 ? (
        <p className="text-[11.5px] text-slate-400 mt-1.5">Nothing to report.</p>
      ) : (
        <ul className="mt-1.5 space-y-1">
          {items.map((t, i) => (
            <li key={i} className="text-[11.5px] text-slate-700 leading-snug pl-2.5 relative">
              <span className="absolute left-0 top-[6px] w-1 h-1 rounded-full bg-slate-400" />{t}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
